import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'
import { cva, VariantProps } from 'class-variance-authority'
import { ExternalLinkIcon } from 'lucide-react'

import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import { formatPercentage } from '@/domain/common/format'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

type DepositActionProps =
  | {
      type: 'connected'
      usdsBalance: NormalizedNumber
      openStakeDialog: () => void
    }
  | {
      type: 'disconnected'
      connectWallet: () => void
      tryInSandbox: () => void
    }

export type DepositCTAPanelProps = {
  apy: Percentage
} & DepositActionProps

export function DepositCTAPanel({ apy, ...actionsProps }: DepositCTAPanelProps) {
  return (
    <Panel
      spacing="m"
      className={cn(
        'flex flex-col justify-between gap-4',
        'bg-cover bg-right bg-no-repeat',
        'bg-[url(/src/ui/assets/spark-staking/stake-cta-panel-bg.svg)] bg-primary-inverse',
      )}
      data-testid={testIds.spkFarm.depositCtaPanel.panel}
    >
      <div className="flex flex-col gap-4">
        <div className="typography-heading-3 sm:typography-heading-2 text-primary-inverse">
          Deposit <TokenBadge variant="usds" />
          <br />
          Earn rewards in <TokenBadge variant="spk" />
        </div>
        <div className="typography-body-3 max-w-[48ch] text-tertiary">
          Deposit USDS and start farming SPK. Withdraw at any time.{' '}
          <ExternalLink to={links.docs.spkFarming} className="inline-flex items-center gap-1">
            Learn more <ExternalLinkIcon className="icon-size-xxs text-farms-600" />
          </ExternalLink>
        </div>
      </div>
      <div className="typography-body-3 text-tertiary sm:mt-12">
        APY:{' '}
        <span className="typography-heading-2 text-primary-inverse">{formatPercentage(apy, { skipSign: true })}</span>
        <span className="typography-heading-2 bg-gradient-spark-primary-2 bg-clip-text text-transparent">%</span>
      </div>
      <Actions {...actionsProps} />
    </Panel>
  )
}

const tokenBadgeVariants = cva(
  'inline-flex h-7 items-center gap-1.5 rounded-full p-2 pr-2.5 sm:h-12 sm:gap-2 sm:pr-3',
  {
    variants: {
      variant: {
        spk: 'bg-[linear-gradient(104.99deg,rgba(255,197,85,0.15)_-21.89%,rgba(251,74,185,0.15)_87.44%)]',
        usds: 'bg-[radial-gradient(160.27%_160.27%_at_50.08%_115.91%,rgba(255,210,50,0.15)_0%,rgba(255,109,109,0.15)_100%)]',
      },
    },
  },
)

const tokenBadgeTextVariants = cva('typography-heading-5 sm:typography-heading-3 bg-clip-text text-transparent', {
  variants: {
    variant: {
      spk: 'bg-gradient-spark-primary-2',
      usds: 'bg-[radial-gradient(160.27%_160.27%_at_50.08%_115.91%,#FFD232_0%,#FF6D6D_100%)]',
    },
  },
})

export interface TokenBadgeProps extends VariantProps<typeof tokenBadgeVariants> {}

function TokenBadge({ variant }: TokenBadgeProps) {
  return (
    <div className={tokenBadgeVariants({ variant })}>
      <img src={variant === 'spk' ? tokenLogos.spk : tokenLogos.usds} className="size-4 rounded-full sm:size-8" />
      <span className={tokenBadgeTextVariants({ variant })}>{variant === 'spk' ? 'SPK' : 'USDS'}</span>
    </div>
  )
}

function Actions(props: DepositActionProps) {
  if (props.type === 'connected') {
    return (
      <Button
        variant="primary"
        size="l"
        className="w-full"
        onClick={props.openStakeDialog}
        disabled={props.usdsBalance.isZero()}
        data-testid={testIds.spkFarm.depositCtaPanel.stakeButton}
      >
        Deposit
      </Button>
    )
  }

  if (props.type === 'disconnected') {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Button variant="primary" size="l" onClick={props.connectWallet}>
          Connect Wallet
        </Button>
        <Button variant="secondary" size="l" onClick={props.tryInSandbox}>
          Try in Sandbox
        </Button>
      </div>
    )
  }
}
